import { getDateTimeFormatter } from '@fe/core';
import { GDFHotPollyTypes, GDFHotPolyRecordNormal } from '../../../gdf/type';
import HotPolyDescriptions from './common/HotPolyDescription';
import HotPolyTitle from './common/HotPolyTitle';

export default function HotPolyTooltip({ record }: { record: GDFHotPolyRecordNormal }) {
    const formatter = getDateTimeFormatter();

    const line2 = Line2(record);
    const line3 = Line3(record);
    const line4 = Line4(record);

    return (
        <div>
            {import.meta.env.DEV && (
                <div>
                    {GDFHotPollyTypes[record.polyType]} ({record.polyType})
                </div>
            )}

            <div>
                <HotPolyTitle type={record.polyType} id={record.id} direction={record.dir} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'max-content auto', columnGap: '1rem' }}>
                {import.meta.env.DEV && record.id !== 0 && (
                    <>
                        <div>ID:</div>
                        <div>{record.id}</div>
                    </>
                )}

                {line2 && (
                    <>
                        <div>{line2[0]}:</div>
                        <div>{line2[1]}</div>
                    </>
                )}

                {line3 && (
                    <>
                        <div>{line3[0]}:</div>
                        <div>{line3[1]}</div>
                    </>
                )}

                {line4 && (
                    <>
                        <div>{line4[0]}:</div>
                        <div>{line4[1]}</div>
                    </>
                )}

                {record.startTime !== null ||
                    (record.endTime !== null && (
                        <>
                            <div>Valid:</div>
                            <div>
                                {formatter.formatTime(record.startTime, 'hm') || '?'} to {formatter.formatTime(record.endTime, 'hm') || '?'}
                            </div>
                        </>
                    ))}
                <HotPolyDescriptions text={record.detail} polyType={record.polyType} />
            </div>
        </div>
    );
}

function Line2(record: GDFHotPolyRecordNormal): [string, string] | null {
    if (
        record.polyType === GDFHotPollyTypes.icing ||
        record.polyType === GDFHotPollyTypes.turbulence ||
        (record.polyType >= GDFHotPollyTypes.turbulence_lowlevel_adv && record.polyType <= GDFHotPollyTypes.turbulence_advisory_obv)
    ) {
        if (record.speed === null) {
            return ['Base', 'Unknown'];
        } else if (record.speed === 0) {
            return ['Base', '< FL240'];
        }
        return ['Base', `FL${(record.speed * 10).toString().padStart(3, '0')}`];
    } else if (record.polyType === GDFHotPollyTypes.jetstream) {
        return ['Speed', `${record.speed} kts`];
    }

    if (!isPlaceholder(record.label)) {
        return ['Label', record.label];
    }

    return null;
}

function Line3(record: GDFHotPolyRecordNormal): [string, string] | null {
    if (record.polyType === GDFHotPollyTypes.tropical_cyclone) {
        if (record.tops > 0) {
            return ['Winds', `${record.tops} kts`];
        } else {
            return ['Winds', '-'];
        }
    } else if (
        record.polyType == GDFHotPollyTypes.icing ||
        record.polyType == GDFHotPollyTypes.turbulence ||
        (record.polyType >= GDFHotPollyTypes.turbulence_lowlevel_adv && record.polyType <= GDFHotPollyTypes.turbulence_advisory_obv)
    ) {
        if (record.tops === 0) {
            return ['Top', 'FL630'];
        } else if (record.tops === null) {
            return ['Top', '-'];
        } else {
            return ['Top', `FL${(record.tops * 10).toString().padStart(3, '0')}`];
        }
    } else if (record.polyType === GDFHotPollyTypes.tstorm) {
        if (record.tops !== null && record.tops !== 0) {
            return ['Top', `FL${(record.tops * 10).toString().padStart(3, '0')}`];
        }
    } else if (record.polyType === GDFHotPollyTypes.trop_height) {
        if (record.tops !== null && record.tops !== 0) {
            return ['Height', (record.tops * 10).toString()];
        }
    } else if (record.polyType === GDFHotPollyTypes.contrail) {
        if (record.tops !== null && record.tops !== 0) {
            return ['Altitude', `FL${(record.tops * 10).toString().padStart(3, '0')}`];
        }
    }

    return null;
}

function Line4(record: GDFHotPolyRecordNormal): [string, string] | null {
    // Icing, turb, can skip they are rendered through custom vector layer
    if (record.polyType === GDFHotPollyTypes.tropical_cyclone) {
        if (record.speed !== null && record.speed !== 0) {
            return ['Speed', `${record.speed} kts`];
        } else {
            return ['Speed', '-'];
        }
    } else if (record.polyType === GDFHotPollyTypes.tstorm) {
        // Except no, because this is part of hotpoly 2
        return ['Vert. Velocity', ''];
    }
    return null;
}

function isPlaceholder(input: string): boolean {
    return !input || input === '-';
}
